// Reuso del execution_id del video creator entre reintentos.
//
// Fix del doble cobro por reintentos (ADR-2026-08-24-doble-cobro-video-creator-
// reintentos): una generación que tarda más que el timeout del front deja el
// clip en error, el usuario reintenta y ANTES cada reintento encolaba (y
// cobraba) un video nuevo. Ahora el front repite el mismo execution_id cuando
// el reintento es del MISMO pedido, y el backend dedupea por idempotency key
// (`creator:{empresa}:{execution_id}`) re-enganchándose al job en curso — o
// devolviendo el video ya generado — sin cobrar de nuevo.
//
// "Mismo pedido" = misma clave de parámetros. La clave usa ids de assets (no
// URLs firmadas, que cambian en cada request) para ser estable entre intentos.

export interface CreatorAttemptRef {
  executionId: string;
  paramsKey: string;
}

/**
 * ref_modes del creator que NO están migrados: van a n8n SIEMPRE, incluso con
 * el flag de atlas activo (ruteo en `videoCreator.ts`). n8n no tiene
 * idempotencia, así que para estos modos el execution_id NO debe conservarse
 * tras un fallo técnico: reusarlo no re-engancha nada — cada reintento genera
 * y cobra un video nuevo igual, y el id guardado sería falsa protección.
 */
export const N8N_ONLY_REF_MODES: ReadonlySet<string> = new Set([
  'dialogo_movimiento',
  'ugc',
]);

export function isN8nOnlyCreatorRefMode(refMode: string): boolean {
  return N8N_ONLY_REF_MODES.has(refMode);
}

export interface CreatorParams {
  prompt?: string;
  duration?: number;
  refMode?: string;
  resolution?: string;
  quality?: string;
  aspectRatio?: string;
  /** Solo relevante en modo personas — pasar null fuera de ese modo (espejo
   *  del body, que solo manda velocidad_voz en personas). */
  speechSpeed?: number | null;
  multiScenes?: Array<{ prompt: string; duration: number }> | null;
  refAssetIds?: string[] | null;
  firstFrameAssetId?: string | null;
  lastFrameAssetId?: string | null;
  voiceId?: string | null;
  dialogue?: string | null;
  dialogueInputMode?: string | null;
  audioUrl?: string | null;
}

export function creatorParamsKey(p: CreatorParams): string {
  return JSON.stringify([
    p.prompt ?? '',
    p.duration ?? null,
    p.refMode ?? 'none',
    p.resolution ?? '720p',
    p.quality ?? 'estandar',
    p.aspectRatio ?? null,
    p.speechSpeed ?? null,
    p.multiScenes ?? null,
    p.refAssetIds ?? null,
    p.firstFrameAssetId ?? null,
    p.lastFrameAssetId ?? null,
    p.voiceId ?? null,
    p.dialogue ?? null,
    p.dialogueInputMode ?? null,
    p.audioUrl ?? null,
  ]);
}

/**
 * Devuelve el ref a usar para este intento: el anterior si es un reintento del
 * mismo pedido (clave igual), o uno nuevo si es un pedido distinto / primero.
 */
export function creatorAttemptRef(
  prev: CreatorAttemptRef | null | undefined,
  paramsKey: string,
): CreatorAttemptRef {
  if (prev && prev.paramsKey === paramsKey) return prev;
  return { executionId: crypto.randomUUID(), paramsKey };
}
